import { NoPermissionState } from "@/components/common/no-permission-state";
import { isForbiddenError } from "@/lib/clinic-grants";
import { useTranslation } from "react-i18next";
import { Pencil, Trash } from "lucide-react";

import {
  EmptyState,
  PageEyebrowLink,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Input,
  Skeleton,
} from "@/components/ui";

import { AlertDialogDelete, FormCategories } from "@/components/common";
import { useFetchFinanceCategoriesQuery, usePermission } from "@/hooks";
import { defaultEntriesCategories } from "@/helpers";
import { useEffect, useState } from "react";

export function FinanceCategory() {
  const { t } = useTranslation();
  // const navigate = useNavigate();
  const { checkPermission } = usePermission();

  const [search, setSearch] = useState('');

  const { data: financeCategoryData, isLoading: isLoadingFinanceCategory, refetch, error: listError } =
    useFetchFinanceCategoriesQuery({
      search
    });

  useEffect(() => {
    setTimeout(() => {
      refetch()
    }, 1500)
  }, [search]);

  // useEffect(() => {
  //   if (!checkPermission("category", "view categories")) {
  //     navigate("/")
  //     return;
  //   }
  // }, []);

  const isEmpty =
    !isLoadingFinanceCategory &&
    Boolean(financeCategoryData) &&
    financeCategoryData?.length === 0;

  // SEC-GATES-02: a refusal from the list endpoint is stated, not shown as an empty list.
  if (isForbiddenError(listError)) return <NoPermissionState />;

  return (
    <section className="space-y-6">
      <PageHeader
        eyebrow={
          <PageEyebrowLink to="/financial/account">
            {t("LABEL.financial")}
          </PageEyebrowLink>
        }
        title={t("LABEL.categories")}
        lede="As categorias que classificam cada entrada e saída do caixa da clínica."
        actions={
          <FormCategories>
            <Button variant="petrol" size="lg" className="px-5">
              {t("LABEL.new")}
            </Button>
          </FormCategories>
        }
      />

      <div className="grid grid-cols-1 max-md:gap-2 md:flex items-center justify-between">
        <Input
          type="search"
          aria-label="Pesquisar categorias"
          placeholder="Pesquisar"
          value={search}
          onChange={e => setSearch(e.target.value)}
          showError={false}
        />
      </div>

      {isEmpty ? (
        <EmptyState
          title="Nenhuma categoria cadastrada"
          description="Cada lançamento financeiro é classificado por uma categoria. Crie a primeira para começar."
          action={
            <FormCategories>
              <Button variant="outline">{t("LABEL.new")}</Button>
            </FormCategories>
          }
        />
      ) : (
      <div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead className="text-right">{t("LABEL.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {financeCategoryData && !isLoadingFinanceCategory
              ? financeCategoryData.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>
                    {defaultEntriesCategories.includes(category.name.toLowerCase())
                      ? <span className="capitalize">{t(`LABEL.${category.name.toLowerCase()}`)}</span>
                      : <span className="capitalize">{category.name}</span>}
                  </TableCell>
                  <TableCell className="flex justify-end items-center gap-2">
                    <FormCategories category={category}>
                      <button className="p-2 rounded bg-zinc-300 hover:bg-zinc-400/50 transition-all text-zinc-600 text-base" disabled={!checkPermission("category", "edit categories")}>
                        <Pencil className="size-4" />
                      </button>
                    </FormCategories>
                    <AlertDialogDelete
                      endpoint={`/finance-categorie-entries/${category.id}`}
                      queryKey="finance-categorie-entries"
                    >
                      <button className="p-2 rounded bg-red-500 hover:bg-red-600 transition-all text-white text-base" disabled={!checkPermission("category", "delete categories")}>
                        <Trash className="size-4" />
                      </button>
                    </AlertDialogDelete>
                  </TableCell>
                </TableRow>
              ))
              : Array.from({ length: 10 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="w-full h-10" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="w-full h-10" />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      )}
    </section>
  );
}
